import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AuthContext } from '../../Header/auth - context';
/* Isti izvor prekršaja kao tab KAZNE AETR — ista pravila, isti tekstovi */
import { ravnaListaPrekrsaja, daniPrekrsaja, danPrekrsaja } from './aetrPrekrsaji';
import { segmentiULokalno, poljeULokalno, oznakaZone, fmtVreme,
         pocetakLokalnogDana, opsegZaUpit, uOpsegu } from './vreme';
import './AktivnostiDijagram.css';

/* Standardne tahograf boje + visina stubića */
const TYPE_META = {
    'break/rest':   { color: '#F5A623', key: 'rest',      h: 14 },
    'shortrest':    { color: '#F5A623', key: 'rest',      h: 14 },
    'availability': { color: '#F7DC12', key: 'available', h: 45 },
    'work':         { color: '#1E56D6', key: 'work',      h: 62 },
    'driving':      { color: '#2E9E2E', key: 'drive',     h: 76 },
    'unknown':      { color: '#E53935', key: 'unknown',   h: 100 },
};
const metaFor = (type) => TYPE_META[type] || TYPE_META.unknown;
const LEGEND = ['rest', 'available', 'work', 'drive', 'unknown'];
const LEGEND_COLOR = { rest: '#F5A623', available: '#F7DC12', work: '#1E56D6', drive: '#2E9E2E', unknown: '#E53935' };

/* Prekršaji koji se NE boje na dijagramu — interval im je ceo prozor pravila,
   a ne stvarni trenutak nastanka, pa bi pojas samo smetao. */
const BEZ_POJASA = ['nightDriving'];

/** ms → HH:MM, prazno ako nema vrednosti (kolone Trajanje/Prekoračenje/Manjak). */
const msHHMM = (ms) => {
    if (ms == null || !isFinite(ms) || ms <= 0) return '';
    const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const hhmm = (min) => {
    if (min == null) return '—';
    const h = Math.floor(min / 60), m = Math.round(min % 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const fmtDate = (val) => {
    if (!val) return '';
    const s = String(val).substring(0, 10);
    const [y, mo, d] = s.split('-');
    return d ? `${Number(d)}.${Number(mo)}.${y}.` : s;
};

const minuteInDay = (iso, day) => {
    if (!iso) return null;
    const s = String(iso);
    if (s.substring(0, 10) !== day) return null;
    const time = s.substring(11, 16);
    if (!time) return null;
    const [h, m] = time.split(':').map(Number);
    return Math.max(0, Math.min(1440, h * 60 + m));
};

/* `propis` = JEDAN ključ iz propisi.js, ili "SVI". Namerno skalar, a ne niz:
   niz napravljen u JSX-u je nov objekat pri svakom renderu, pa bi ulazio u
   zavisnosti efekta i vrteo fetch u krug. Niz se gradi ovde, uz poziv. */
const AktivnostiDijagram = ({ data, selectedCard, dateFrom, dateTo, propis, idDrzava }) => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const company = useSelector(state => state.company);

    const [openDetails, setOpenDetails] = useState(null);
    /* Više dana može imati otvorene prekršaje istovremeno — otvaranje jednog
       ne zatvara prethodne. */
    const [openViolations, setOpenViolations] = useState(() => new Set());

    const toggleViolations = (day) => setOpenViolations(prev => {
        const next = new Set(prev);
        if (next.has(day)) next.delete(day); else next.add(day);
        return next;
    });
    const [vehicles, setVehicles] = useState([]);
    const [places, setPlaces] = useState([]);
    const [faults, setFaults] = useState([]);      // [{Vozač, Poruka, _day, _tip}]
    const [navIdx, setNavIdx] = useState(0);
    const dayRefs = useRef({});

    const toISO = d => d instanceof Date
        ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        : (d || '');

    // Dopunski podaci: vozila (sivi bar), lokacije (ovalni markeri), prekršaji (AETR)
    useEffect(() => {
        if (!selectedCard) { setVehicles([]); setPlaces([]); setFaults([]); return; }
        /* Od baze se traži jedan dan više sa svake strane — lokalni dan zahvata
           dva UTC dana, pa bez toga fali prva 2h prvog i poslednja 2h zadnjeg dana.
           Prikaz se posle skraćuje na traženi opseg (`uOpsegu`, `prikazOd/Do`). */
        const upit = opsegZaUpit(toISO(dateFrom), toISO(dateTo));
        const body = JSON.stringify({ ...upit, employeeId: selectedCard });
        const headers = { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token };
        const post = (url) => fetch(process.env.REACT_APP_BACKEND_URL + url, { method: 'POST', headers, body })
            .then(r => r.ok ? r.json() : null).catch(() => null);

        /* i vozila i mesta nose vremena — moraju u istu zonu kao aktivnosti,
           inače bi se oznaka države pojavila na pogrešnom satu ose */
        post('/tacho/vehicles').then(d => setVehicles(
            (Array.isArray(d) ? d : []).map(x => poljeULokalno(x, ['UseBegin', 'UseEnd']))));
        post('/tacho/places').then(d => setPlaces(
            (Array.isArray(d) ? d : []).map(x => poljeULokalno(x, ['EntryTime']))));

        post('/tacho/faults').then(d => {
            if (!d || !Array.isArray(d.records) || d.records.length === 0) { setFaults([]); return; }
            try {
                setFaults(ravnaListaPrekrsaja({
                    records: d.records,
                    cards: d.cards,
                    companyName: company?.companyName ?? '',
                    dateTo,
                    // zapisi stižu i pre traženog datuma — prikaz ostaje u periodu
                    /* prikaz ostaje na onome što je korisnik tražio, iako je upit širi */
                    prikazOd: toISO(dateFrom),
                    prikazDo: toISO(dateTo),
                    propisi: propis && propis !== 'SVI' ? [propis] : null,
                    idDrzava,
                }));
            } catch (e) {
                console.error('AETR analiza (dijagram):', e);
                setFaults([]);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCard, dateFrom, dateTo, auth.token, propis, idDrzava]);

    /* Tahograf beleži u UTC, a i vozač i inspektor gledaju zidni sat — kontrolni
       izveštaj piše „CET/CEST". Zapisi se zato JEDNOM prebace u lokalnu zonu, pa
       ostatak komponente radi nepromenjen.

       Aktivnosti ovde stižu kao `ActivityDate` + `StartMinute` + `Duration`, gde su
       minuti mereni od UTC ponoći — zato `segmentiULokalno`, a NE `zapisiULokalno`
       (ta radi nad StartTime/EndTime, kojih u ovom skupu nema; prva verzija je
       pozivala nju i zato se na dijagramu nije pomerilo ništa).

       Segment koji pređe lokalnu ponoć se seče na dva dana, pa aktivnost od
       22.06 23:25 UTC završi na 23.06 u 01:25 — tačno kao u kontrolnom izveštaju. */
    const rows = useMemo(() => {
        const od = toISO(dateFrom), doo = toISO(dateTo);
        return segmentiULokalno(Array.isArray(data) ? data : [])
            .filter(r => uOpsegu(String(r.ActivityDate).substring(0, 10), od, doo));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, dateFrom, dateTo]);

    // Grupisanje aktivnosti po danu
    const { byDay, days, holder } = useMemo(() => {
        const bd = {};
        rows.forEach(r => {
            const day = String(r.ActivityDate).substring(0, 10);
            (bd[day] = bd[day] || []).push(r);
        });
        return {
            byDay: bd,
            days: Object.keys(bd).sort(),
            holder: rows[0]?.HolderName || rows[0]?.CardNumber || '',
        };
    }, [rows]);

    /* Prekršaj se BROJI samo jednom — na danu u kome se period završava
       (radni period i odmor redovno prelaze ponoć). */
    const faultsByDay = useMemo(() => {
        const m = {};
        faults.forEach(f => {
            const d = danPrekrsaja(f);
            if (d) (m[d] = m[d] || []).push(f);
        });
        return m;
    }, [faults]);

    /* Crveni pojas se, za razliku od brojanja, crta na SVAKOM danu koji interval
       dodiruje — da se vidi odakle dokle je prekršaj nastao.
       Noćna vožnja se izuzima: njen "interval" je ceo noćni prozor (00:00–04:00),
       pa bi pojas prekrio pola dijagrama bez korisne informacije. */
    const bandsByDay = useMemo(() => {
        const m = {};
        faults
            .filter(f => !BEZ_POJASA.includes(f._tip))
            .forEach(f => daniPrekrsaja(f).forEach(d => { (m[d] = m[d] || []).push(f); }));
        return m;
    }, [faults]);

    const faultDays = useMemo(() => days.filter(d => (faultsByDay[d] || []).length > 0), [days, faultsByDay]);

    const totalFaults = faults.length;

    const gotoFaultDay = (dir) => {
        if (faultDays.length === 0) return;
        const next = (navIdx + dir + faultDays.length) % faultDays.length;
        setNavIdx(next);
        const day = faultDays[next];
        const el = dayRefs.current[day];
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('ad-day--flash');
            setTimeout(() => el.classList.remove('ad-day--flash'), 1200);
        }
        // navigacija strelicama otvara taj dan, a već otvorene ostavlja otvorene
        setOpenViolations(prev => new Set(prev).add(day));
    };

    if (!selectedCard) return <div className="ad-empty">{t('taho.izaberiJednogVozaca')}</div>;
    if (rows.length === 0) return <div className="ad-empty">{t('taho.nemaAktivnosti')}</div>;

    const dayTotals = (segs) => {
        const tot = {};
        segs.forEach(s => {
            const k = metaFor(s.ActivityType).key;
            tot[k] = (tot[k] || 0) + (Number(s.Duration) || 0);
        });
        return tot;
    };

    /* Radni dan = od početka prve do kraja poslednje aktivnosti koja nije odmor
       (vožnja / rad / raspoloživost) — koliko je vozač bio "na poslu" tog dana. */
    const radniDan = (segs) => {
        const radne = segs.filter(s => {
            const k = metaFor(s.ActivityType).key;
            return (k === 'drive' || k === 'work' || k === 'available') && Number(s.Duration) > 0;
        });
        if (radne.length === 0) return null;
        const pocetak = Math.min(...radne.map(s => Number(s.StartMinute) || 0));
        const kraj = Math.max(...radne.map(s => (Number(s.StartMinute) || 0) + (Number(s.Duration) || 0)));
        return { pocetak, kraj, trajanje: kraj - pocetak };
    };

    const hours = Array.from({ length: 25 }, (_, i) => i);

    return (
        <div className="ad-root">
            {/* Zaglavlje perioda: naslov + ukupan broj prekršaja (crveno) + navigacija */}
            <div className="ad-period-head">
                <span className="ad-period-title">{t('taho.aktivnostVozaca')}</span>
                <span className="ad-period-holder">{holder}</span>
                {totalFaults > 0 ? (
                    <span className="ad-period-faults">
                        <span className="ad-faults-count">{t('taho.prekrsaji')}: {totalFaults}</span>
                        <button type="button" className="ad-nav-btn" title={t('taho.prethodniPrekrsaj')}
                            onClick={() => gotoFaultDay(-1)}>◀</button>
                        <span className="ad-nav-pos">{faultDays.length ? `${navIdx + 1}/${faultDays.length}` : ''}</span>
                        <button type="button" className="ad-nav-btn" title={t('taho.sledeciPrekrsaj')}
                            onClick={() => gotoFaultDay(1)}>▶</button>
                    </span>
                ) : (
                    <span className="ad-period-ok">{t('taho.nemaPrekrsaja')}</span>
                )}
            </div>

            {days.map(day => {
                const segs = byDay[day].slice().sort((a, b) => (a.StartMinute || 0) - (b.StartMinute || 0));
                const tot = dayTotals(segs);
                const dayFaults = faultsByDay[day] || [];

                const dayVehicles = vehicles.filter(v =>
                    String(v.UseBegin || '').substring(0, 10) === day || String(v.UseEnd || '').substring(0, 10) === day);
                const dayPlaces = places.filter(p => String(p.EntryTime || '').substring(0, 10) === day);

                const dayKm = dayVehicles.reduce((acc, v) => {
                    const b = Number(v.OdometerBeginKm), e = Number(v.OdometerEndKm);
                    return acc + (isFinite(b) && isFinite(e) && e >= b ? e - b : 0);
                }, 0);

                /* '?' (nepoznata država) ima smisla samo ako je tog dana bilo stvarne
                   aktivnosti — vožnja / rad / raspoloživost. Za dane koji su samo
                   odmor (ili nepoznato) marker se ne prikazuje. */
                const imaAktivnost = segs.some(s => {
                    const k = metaFor(s.ActivityType).key;
                    return (k === 'drive' || k === 'work' || k === 'available') && Number(s.Duration) > 0;
                });

                return (
                    <div key={day} className="ad-day" ref={el => { dayRefs.current[day] = el; }}>
                        <div className="ad-day-meta">
                            <span><b>{t('taho.datum')}:</b> {fmtDate(day)}</span>
                            {dayKm > 0 && <span><b>{t('taho.dnevnaKilometraza')}:</b> {dayKm} km</span>}
                            {LEGEND.map(k => tot[k] ? (
                                <span key={k}>
                                    <b style={{ color: k === 'available' ? '#b59f00' : LEGEND_COLOR[k] }}>{t(`taho.leg_${k}`)}:</b> {hhmm(tot[k])}
                                </span>
                            ) : null)}
                            {(() => {
                                const rd = radniDan(segs);
                                return rd ? (
                                    <span title={`${hhmm(rd.pocetak)} – ${hhmm(rd.kraj)}`}>
                                        <b>{t('taho.radniDan')}:</b> {hhmm(rd.trajanje)} ({hhmm(rd.pocetak)}–{hhmm(rd.kraj)})
                                    </span>
                                ) : null;
                            })()}
                            {dayFaults.length > 0 && <span className="ad-day-faultbadge">{t('taho.prekrsaji')}: {dayFaults.length}</span>}
                        </div>

                        <div className="ad-chart-row">
                            <div className="ad-chart-wrap">
                                <div className="ad-plot">
                                    {dayVehicles.map((v, i) => {
                                        const s = minuteInDay(v.UseBegin, day) ?? 0;
                                        const e = minuteInDay(v.UseEnd, day) ?? 1440;
                                        if (e <= s) return null;
                                        return (
                                            <div key={`v${i}`} className="ad-vehicle-bar"
                                                style={{ left: `${(s / 1440) * 100}%`, width: `${((e - s) / 1440) * 100}%` }}>
                                                <span className="ad-vehicle-reg">{v.VehicleRegistrationNumber || ''}</span>
                                            </div>
                                        );
                                    })}

                                    {segs.map((s, i) => {
                                        const start = Number(s.StartMinute) || 0;
                                        const dur = Number(s.Duration) || 0;
                                        if (dur <= 0) return null;
                                        const meta = metaFor(s.ActivityType);
                                        return (
                                            <div key={i} className="ad-seg"
                                                style={{
                                                    left: `${(start / 1440) * 100}%`,
                                                    width: `${(dur / 1440) * 100}%`,
                                                    height: `${meta.h}%`,
                                                    background: meta.color,
                                                }}
                                                title={`${hhmm(start)} – ${hhmm(start + dur)} · ${t(`taho.leg_${meta.key}`)}`} />
                                        );
                                    })}

                                    {/* Ovalni markeri država; ako za taj dan nema podatka → '?' */}
                                    {dayPlaces.length > 0 ? dayPlaces.map((p, i) => {
                                        const m = minuteInDay(p.EntryTime, day);
                                        if (m == null) return null;
                                        return (
                                            <div key={`p${i}`} className="ad-place" style={{ left: `${(m / 1440) * 100}%` }}
                                                title={`${p.Drzava || t('taho.nepoznataDrzava')} · ${String(p.EntryTime).substring(11, 16)}`}>
                                                <span className="ad-place-oval">{p.Drzava || '?'}</span>
                                            </div>
                                        );
                                    }) : (imaAktivnost && (
                                        <div className="ad-place" style={{ left: '2%' }} title={t('taho.nemaPodatkaDrzava')}>
                                            <span className="ad-place-oval">?</span>
                                        </div>
                                    ))}

                                    {/* Crveni pojas — tačno od kada do kada je prekršaj nastao */}
                                    {(bandsByDay[day] || []).map((f, i) => {
                                        if (f._od == null || f._do == null) return null;
                                        /* `day` je LOKALAN dan, a `f._od/_do` su stvarni UTC —
                                           pa početak dana mora biti UTC trenutak lokalne ponoći.
                                           Sa `Date.parse(day + 'Z')` obe strane oduzimanja nisu
                                           u istom okviru i pojas je padao 2h pored stubića. */
                                        const pocetakDana = pocetakLokalnogDana(day);
                                        const od = Math.max(0, Math.round((f._od - pocetakDana) / 60000));
                                        const doo = Math.min(1440, Math.round((f._do - pocetakDana) / 60000));
                                        if (doo <= od) return null;
                                        return (
                                            <div key={`f${i}`} className="ad-fault-band"
                                                style={{ left: `${(od / 1440) * 100}%`, width: `${((doo - od) / 1440) * 100}%` }}
                                                title={`${hhmm(od)} – ${hhmm(doo)} · ${f.Poruka}`} />
                                        );
                                    })}

                                    {hours.map(h => <div key={h} className="ad-tick" style={{ left: `${(h / 24) * 100}%` }} />)}
                                </div>

                                <div className="ad-axis">
                                    {hours.map(h => <span key={h} className="ad-hour" style={{ left: `${(h / 24) * 100}%` }}>{h}</span>)}
                                </div>
                                <div className="ad-axis-row">
                                    <span className="ad-axis-date">{fmtDate(day)}</span>
                                    <span className="ad-axis-label">{t('taho.vremeLokalno', { zona: oznakaZone() })}</span>
                                    <span />
                                </div>
                            </div>

                            <div className="ad-legend">
                                {LEGEND.map(k => (
                                    <div key={k} className="ad-legend-item">
                                        <span className="ad-legend-box" style={{ background: LEGEND_COLOR[k] }} />
                                        <span>{t(`taho.leg_${k}`)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="ad-links">
                            <button type="button" className="ad-link"
                                onClick={() => setOpenDetails(openDetails === day ? null : day)}>
                                {openDetails === day ? t('taho.sakrijDetalje') : t('taho.prikaziDetalje')}
                            </button>
                            <button type="button"
                                className={`ad-link${dayFaults.length > 0 ? ' ad-link--fault' : ''}`}
                                onClick={() => toggleViolations(day)}>
                                {openViolations.has(day) ? t('taho.sakrijPrekrsaje') : t('taho.prikaziPrekrsaje')}
                                {dayFaults.length > 0 ? ` (${dayFaults.length})` : ''}
                            </button>
                        </div>

                        {openDetails === day && (
                            <table className="ad-detail-table">
                                <thead>
                                    <tr>
                                        <th>{t('taho.pocetak')}</th>
                                        <th>{t('taho.kraj')}</th>
                                        <th>{t('taho.trajanje')}</th>
                                        <th>{t('taho.aktivnost')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {segs.map((s, i) => {
                                        const start = Number(s.StartMinute) || 0, dur = Number(s.Duration) || 0;
                                        const meta = metaFor(s.ActivityType);
                                        return (
                                            <tr key={i}>
                                                <td>{hhmm(start)}</td>
                                                <td>{hhmm(start + dur)}</td>
                                                <td>{hhmm(dur)}</td>
                                                <td><span className="ad-dot" style={{ background: meta.color }} />{t(`taho.leg_${meta.key}`)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}

                        {openViolations.has(day) && (
                            dayFaults.length === 0
                                ? <div className="ad-noviol">{t('taho.nemaPrekrsajaDan')}</div>
                                : (
                                    /* Strukturisan prikaz: vreme, vrsta, trajanje, prekoračenje,
                                       manjak. Pun tekst sa iznosom kazne je u tooltipu reda. */
                                    <table className="ad-detail-table ad-viol-table">
                                        <thead>
                                            <tr>
                                                <th>{t('taho.vreme')}</th>
                                                <th>{t('taho.vrstaPrekrsaja')}</th>
                                                <th>{t('taho.podvrsta')}</th>
                                                <th>{t('taho.trajanje')}</th>
                                                <th>{t('taho.prekoracenje')}</th>
                                                <th>{t('taho.manjak')}</th>
                                                <th>{t('taho.kazna')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {dayFaults.map((f, i) => (
                                                <tr key={i} className="ad-bad" title={f._opis || f.Poruka}>
                                                    <td>{f.Vreme ? fmtVreme(f.Vreme) : '—'}</td>
                                                    <td>{f.Tip || f._grupa}</td>
                                                    <td>{f.Podtip || ''}</td>
                                                    <td>{msHHMM(f.Trajanje)}</td>
                                                    <td className="ad-viol-num">{msHHMM(f.Prekoracenje)}</td>
                                                    <td className="ad-viol-num">{msHHMM(f.Manjak)}</td>
                                                    <td className="ad-viol-kazna">{f._kazna || ''}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default AktivnostiDijagram;
